import { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import MentorshipCareerTrackModal, { MentorshipCareerTrackModalValue } from './MentorshipCareerTrackModal';
import MentorshipService, {
    MentorshipMappingState,
    MentorshipTaxonomyChild,
    MentorshipTaxonomyTrack,
    emptyMappingState,
} from './MentorshipService';

const POLL_INTERVAL_MS = 5000;
const COUNT_UNAVAILABLE_LABEL = 'Not Yet Calculated / Unavailable';
const STATUS_NOT_YET_CALCULATED = 'Not Yet Calculated';
const STATUS_CALCULATING = 'Calculating';
const STATUS_FAILED = 'Unavailable / Calculation Failed';

interface MentorshipDomainTaxonomyProps {
    cohortId: number | null;
    disabled?: boolean;
    /** Fired after a successful save so gates that depend on track count can re-read it. */
    onTracksSaved?: () => void;
}

interface DraftChild {
    localKey: string;
    id?: number;
    name: string;
}

interface DraftTrack {
    localKey: string;
    id?: number;
    name: string;
    children: DraftChild[];
    potentialMentorCount: number | null;
}

const childrenToCommaText = (children: DraftChild[]): string =>
    children.map((child) => child.name ?? '').join(', ');

const parseChildNames = (childrenText: string): string[] =>
    String(childrenText || '')
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

const childrenFromCommaText = (childrenText: string, existing: DraftChild[], nextKey: () => string): DraftChild[] => {
    const unused = existing.filter((child) => child.id != null);
    return parseChildNames(childrenText).map((name) => {
        const matchIndex = unused.findIndex((child) => child.name === name);
        if (matchIndex >= 0) {
            const [match] = unused.splice(matchIndex, 1);
            return { localKey: match.localKey, id: match.id, name };
        }
        return { localKey: nextKey(), name };
    });
};

const toDraftTracks = (tracks: MentorshipTaxonomyTrack[], nextKey: () => string): DraftTrack[] =>
    (Array.isArray(tracks) ? tracks : []).map((track) => ({
        localKey: track.id != null ? `track-${track.id}` : nextKey(),
        id: track.id,
        name: track.name ?? '',
        potentialMentorCount: track.potentialMentorCount == null ? null : track.potentialMentorCount,
        children: (Array.isArray(track.children) ? track.children : []).map((child: MentorshipTaxonomyChild) => ({
            localKey: child.id != null ? `child-${child.id}` : nextKey(),
            id: child.id,
            name: child.name ?? '',
        })),
    }));

const countDisplay = (count: number | null): string => {
    if (count == null) return COUNT_UNAVAILABLE_LABEL;
    return String(count);
};

const mappingStatusDisplay = (mapping: MentorshipMappingState): string | null => {
    if (mapping.status === 'QUEUED' || mapping.status === 'RUNNING') return STATUS_CALCULATING;
    if (mapping.status === 'FAILED') return STATUS_FAILED;
    if (mapping.status == null) return STATUS_NOT_YET_CALCULATED;
    return null;
};

const MentorshipDomainTaxonomy = ({ cohortId, disabled = false, onTracksSaved }: MentorshipDomainTaxonomyProps) => {
    const keySeq = useRef(0);
    const nextKey = () => {
        keySeq.current += 1;
        return `taxonomy-draft-${keySeq.current}`;
    };

    const [draftTracks, setDraftTracks] = useState<DraftTrack[]>([]);
    const [mapping, setMapping] = useState<MentorshipMappingState>(emptyMappingState());
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [showRecalculateConfirm, setShowRecalculateConfirm] = useState(false);
    const [completionMessage, setCompletionMessage] = useState('');
    /** Set once this admin starts a run, so its completion can be announced when polling sees it. */
    const awaitingCompletion = useRef(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [editingKey, setEditingKey] = useState<string | null>(null);

    const cohortAvailable = cohortId != null;
    const actionsDisabled = disabled || !cohortAvailable || isLoading || isSaving;
    const recalculateLockedUntil = mapping.recalculateLocked
        ? MentorshipService.formatLastCalculatedAt(mapping.recalculateAvailableAt)
        : null;
    const recalculateDisabled = actionsDisabled || isRecalculating || mapping.recalculateLocked;
    /** Hide Recalculate/Save until at least one career track exists in the draft (R-QA-01 intake 16). */
    const showSaveAndRecalculate = draftTracks.length > 0;
    const statusLabel = mappingStatusDisplay(mapping);
    const lastCalculatedLabel = MentorshipService.formatLastCalculatedAt(mapping.lastCalculatedAt);

    const applyPollUpdate = (tracks: MentorshipTaxonomyTrack[], nextMapping: MentorshipMappingState) => {
        setMapping(nextMapping);
        setDraftTracks((current) =>
            current.map((track) => {
                if (track.id == null) return track;
                const server = tracks.find((item) => item.id === track.id);
                return {
                    ...track,
                    potentialMentorCount: server?.potentialMentorCount == null ? null : server.potentialMentorCount,
                };
            })
        );
    };

    useEffect(() => {
        setShowRecalculateConfirm(false);
        setCompletionMessage('');
        awaitingCompletion.current = false;
        if (cohortId == null) {
            setDraftTracks([]);
            setMapping(emptyMappingState());
            setErrorMessage('');
            setIsLoading(false);
            setShowModal(false);
            setEditingKey(null);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setErrorMessage('');
            setShowModal(false);
            setEditingKey(null);
            try {
                const taxonomy = await MentorshipService.getDomainTaxonomy(cohortId);
                if (!cancelled) {
                    setDraftTracks(toDraftTracks(taxonomy.tracks, nextKey));
                    setMapping(taxonomy.mapping);
                }
            } catch (error) {
                if (!cancelled) {
                    setDraftTracks([]);
                    setMapping(emptyMappingState());
                    setErrorMessage(MentorshipService.getErrorMessage(error));
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [cohortId]);

    useEffect(() => {
        if (cohortId == null) return;
        if (mapping.status !== 'QUEUED' && mapping.status !== 'RUNNING') return;

        let cancelled = false;
        const timer = window.setInterval(() => {
            MentorshipService.getDomainTaxonomy(cohortId)
                .then((taxonomy) => {
                    if (!cancelled) {
                        applyPollUpdate(taxonomy.tracks, taxonomy.mapping);
                    }
                })
                .catch(() => undefined);
        }, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [cohortId, mapping.status]);

    // The existing poll updates mapping.status; announce the finish only for a run this admin started.
    useEffect(() => {
        if (!awaitingCompletion.current) return;
        if (mapping.status === 'COMPLETED') {
            awaitingCompletion.current = false;
            setCompletionMessage(
                'Domain Taxonomy recalculation is complete. The potential mentor counts below are up to date.'
            );
        } else if (mapping.status === 'FAILED') {
            awaitingCompletion.current = false;
        }
    }, [mapping.status]);

    const editingTrack = draftTracks.find((track) => track.localKey === editingKey) || null;

    const openAdd = () => {
        if (actionsDisabled) return;
        setModalMode('add');
        setEditingKey(null);
        setShowModal(true);
    };

    const openEdit = (track: DraftTrack) => {
        if (actionsDisabled) return;
        setModalMode('edit');
        setEditingKey(track.localKey);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingKey(null);
    };

    const applyModal = (value: MentorshipCareerTrackModalValue) => {
        const name = value?.name ?? '';
        if (modalMode === 'edit' && editingKey) {
            setDraftTracks((current) =>
                current.map((track) =>
                    track.localKey === editingKey
                        ? {
                              ...track,
                              name,
                              children: childrenFromCommaText(value.childrenText, track.children, nextKey),
                          }
                        : track
                )
            );
        } else {
            setDraftTracks((current) => [
                ...current,
                {
                    localKey: nextKey(),
                    name,
                    children: childrenFromCommaText(value.childrenText, [], nextKey),
                    potentialMentorCount: null,
                },
            ]);
        }
        closeModal();
    };

    const handleSave = async () => {
        if (cohortId == null || actionsDisabled) return;
        setIsSaving(true);
        setErrorMessage('');
        try {
            const saved = await MentorshipService.saveDomainTaxonomy(cohortId, {
                tracks: draftTracks.map((track) => {
                    const row: MentorshipTaxonomyTrack = {
                        name: track.name ?? '',
                        children: track.children.map((child) => {
                            const item: MentorshipTaxonomyChild = { name: child.name ?? '' };
                            if (child.id != null) {
                                item.id = child.id;
                            }
                            return item;
                        }),
                    };
                    if (track.id != null) {
                        row.id = track.id;
                    }
                    return row;
                }),
            });
            setDraftTracks(toDraftTracks(saved.tracks, nextKey));
            const latest = await MentorshipService.getDomainTaxonomy(cohortId);
            setDraftTracks(toDraftTracks(latest.tracks, nextKey));
            setMapping(latest.mapping);
            onTracksSaved?.();
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    // Recalculate asks for confirmation first; nothing is sent until the admin confirms.
    const openRecalculateConfirm = () => {
        if (cohortId == null || recalculateDisabled) return;
        setShowRecalculateConfirm(true);
    };

    const handleRecalculate = async () => {
        setShowRecalculateConfirm(false);
        if (cohortId == null || recalculateDisabled) return;
        setIsRecalculating(true);
        setErrorMessage('');
        setCompletionMessage('');
        try {
            await MentorshipService.recalculateDomainTaxonomy(cohortId);
            awaitingCompletion.current = true;
            const latest = await MentorshipService.getDomainTaxonomy(cohortId);
            applyPollUpdate(latest.tracks, latest.mapping);
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
            // A stale page can still send a click the server rejects (7-day lock); re-read so the
            // button reflects the lock. Counts only - unsaved track edits are kept.
            try {
                const latest = await MentorshipService.getDomainTaxonomy(cohortId);
                applyPollUpdate(latest.tracks, latest.mapping);
            } catch {
                /* keep the original error on screen */
            }
        } finally {
            setIsRecalculating(false);
        }
    };

    return (
        <div className="d-flex flex-column gap-3">
            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {(isRecalculating || mapping.status === 'QUEUED' || mapping.status === 'RUNNING') && (
                <div className="alert alert-info d-flex align-items-start gap-2 mb-0" role="status">
                    <span className="spinner-border spinner-border-sm mt-1 flex-shrink-0" aria-hidden="true" />
                    <div>
                        <div className="fw-semibold">Please wait — Domain Taxonomy is being recalculated.</div>
                        <div className="small">
                            Alumni are being mapped to each career track in the background. This can take a few
                            hours for a large alumni base. The potential mentor counts will update automatically when
                            it finishes, and you can safely leave this page.
                        </div>
                    </div>
                </div>
            )}
            {completionMessage && (
                <div className="alert alert-success mb-0" role="status">
                    {completionMessage}
                </div>
            )}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                    <h3 className="font-14 font-700 text-dark">Domain Taxonomy</h3>
                    {statusLabel && <div className="font-12 text-secondary mt-1">{statusLabel}</div>}
                    {lastCalculatedLabel && (
                        <div className="font-12 text-secondary mt-1">Last calculated: {lastCalculatedLabel}</div>
                    )}
                    {mapping.recalculateLocked && showSaveAndRecalculate && (
                        <div className="font-12 text-secondary mt-1">
                            Recalculate can be run once every 7 days
                            {recalculateLockedUntil ? ` — available again on ${recalculateLockedUntil}` : ''}
                        </div>
                    )}
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-outline-primary" disabled={actionsDisabled} onClick={openAdd}>
                        Add Career Track
                    </button>
                    {showSaveAndRecalculate && (
                        <>
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                disabled={recalculateDisabled}
                                title={
                                    recalculateLockedUntil
                                        ? `Recalculate is available again on ${recalculateLockedUntil}`
                                        : undefined
                                }
                                onClick={openRecalculateConfirm}>
                                Recalculate
                            </button>
                            <button
                                type="button"
                                className="btn btn-success"
                                disabled={actionsDisabled}
                                onClick={handleSave}>
                                Save Settings
                            </button>
                        </>
                    )}
                </div>
            </div>

            {!cohortAvailable && <div className="text-muted mb-3">Select a cohort to manage Domain Taxonomy.</div>}
            {cohortAvailable && isLoading && <div className="text-muted mb-3">Loading taxonomy...</div>}
            {cohortAvailable && !isLoading && draftTracks.length === 0 && (
                <div className="text-muted mb-3">No career tracks yet.</div>
            )}

            {draftTracks.map((track) => (
                <div key={track.localKey} className="card border border-light-subtle rounded-3 shadow-sm bg-white overflow-hidden ">
                    <div className="card-header bg-light border-bottom d-flex align-items-center justify-content-between px-3 py-2.5">
                        <div>
                            <div className="font-14 font-700 text-dark">{track.name || '\u00a0'}</div>
                            <div className="small text-muted">Potential mentor count: {countDisplay(track.potentialMentorCount)}</div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-link text-primary font-12 font-500 p-0 text-decoration-none"
                            disabled={actionsDisabled}
                            onClick={() => openEdit(track)}>
                            Edit
                        </button>
                    </div>
                    <div className="card-body p-3 d-flex flex-wrap gap-2">
                        {track.children.length === 0 && (
                            <span className="small text-muted">No child / sub-domain values <span className="text-secondary ms-1 cursor-pointer hover-danger" >×</span></span>
                        )}
                        {track.children.map((child) => (
                            <span key={child.localKey} className="badge bg-light text-dark border">
                                {child.name || '\u00a0'} <span className="text-secondary ms-1 cursor-pointer hover-danger" onClick={() => {
                                    const badge = document.getElementById(child.localKey);
                                    if (badge) {
                                        badge.remove();
                                    }
                                }}>×</span>
                            </span>
                        ))}
                        <span onClick={() => openEdit(track)} className="badge bg-white text-primary border border-primary border-dashed px-2 py-1.5 font-12 rounded-3 cursor-pointer" >+ Add sub-domain</span>
                    </div>
                </div>
            ))}

            <Modal show={showRecalculateConfirm} onHide={() => setShowRecalculateConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Recalculate Domain Taxonomy?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        This will re-map every alumni profile at this cohort&apos;s campuses to the career tracks and
                        refresh the potential mentor count for each track.
                    </p>
                    <p className="mb-0">
                        <strong>Recalculate can be run only once every 7 days for a cohort.</strong> Once you confirm,
                        the Recalculate button will be disabled for this cohort for the next 7 days. The calculation
                        runs in the background and may take a few hours to complete.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowRecalculateConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" disabled={isRecalculating} onClick={handleRecalculate}>
                        Yes, Recalculate
                    </Button>
                </Modal.Footer>
            </Modal>

            <MentorshipCareerTrackModal
                show={showModal}
                mode={modalMode}
                initialName={editingTrack?.name ?? ''}
                initialChildrenText={childrenToCommaText(editingTrack?.children || [])}
                onClose={closeModal}
                onApply={applyModal}
            />
        </div>
    );
};

export default MentorshipDomainTaxonomy;
